import { useForm } from "@inertiajs/react";
import LogoUpload from "@/components/LogoUpload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { bizminer_admin_organization_path } from "@/routes";
import type { BizminerAdminOrganization } from "@/types/serializers";

interface EditOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization: BizminerAdminOrganization;
}

export function EditOrganizationDialog({
  open,
  onOpenChange,
  organization,
}: EditOrganizationDialogProps) {
  const { data, setData, patch, processing, errors, reset, transform } =
    useForm({
      organization: {
        name: organization.name || "",
        website: organization.website || "",
        description: organization.description || "",
        logo: null as File | "" | null,
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    transform((formData) => {
      const submitData: Record<string, unknown> = {
        organization: {
          name: formData.organization.name,
          website: formData.organization.website,
          description: formData.organization.description,
        },
      };

      if (formData.organization.logo !== null) {
        (submitData.organization as Record<string, unknown>).logo =
          formData.organization.logo;
      }

      return submitData;
    });

    patch(bizminer_admin_organization_path(organization.id), {
      forceFormData: !!data.organization.logo,
      onSuccess: () => {
        handleClose();
      },
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px] text-foreground">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update organization details. The slug cannot be changed.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="org-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="org-name"
                type="text"
                value={data.organization.name}
                onChange={(e) => setData("organization.name", e.target.value)}
                placeholder="Enter organization name"
                required
                autoFocus
              />
              {errors["organization.name"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.name"]}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="org-slug">Slug</Label>
              <Input
                id="org-slug"
                type="text"
                value={organization.slug}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                The slug cannot be changed after creation.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="org-website">Website</Label>
              <Input
                id="org-website"
                type="url"
                value={data.organization.website}
                onChange={(e) =>
                  setData("organization.website", e.target.value)
                }
                placeholder="https://example.com"
              />
              {errors["organization.website"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.website"]}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="org-description">Description</Label>
              <Textarea
                id="org-description"
                value={data.organization.description}
                onChange={(e) =>
                  setData("organization.description", e.target.value)
                }
                placeholder="Brief description of the organization"
                rows={3}
              />
              {errors["organization.description"] && (
                <p className="text-sm text-destructive">
                  {errors["organization.description"]}
                </p>
              )}
            </div>

            <LogoUpload
              currentLogoUrl={organization.logo_url}
              onLogoChange={(file) => setData("organization.logo", file)}
              error={errors["organization.logo"]}
              label="Logo"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing || !data.organization.name}
            >
              {processing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
